import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NO_VALUE_DASH } from "@ryde/shared";
import { blueMui, greenMui } from "@ryde/theme";

import { IPlaybackModel } from "../investigation.models";
import {
  PanelSectionStyled,
  PanelSectionTitleStyled,
  SparkWrapStyled,
  StatGridStyled,
} from "../investigation.panel.styled";
import { buildOccupancyPath, buildSpeedPath } from "../investigation.utils";
import { InvestigationSparkline } from "./investigation.sparkline";
import { InvestigationStatTile } from "./investigation.stat-tile";

/** Passenger-flow tiles plus on-board and speed strips over the ride. */
export const InvestigationPassengerStats: FC<{ model: IPlaybackModel }> = ({
  model,
}) => {
  const { t } = useTranslation();
  const summary = model.payload.summary;
  const getLabel = (key: string): string =>
    t(`rideMap.investigation.passengers.${key}`);
  const peak = useMemo(
    () => model.occupancy.reduce((max, point) => Math.max(max, point.count), 0),
    [model],
  );
  const occupancyPath = useMemo(
    () => buildOccupancyPath(model, peak),
    [model, peak],
  );
  const speedPath = useMemo(() => buildSpeedPath(model), [model]);

  return (
    <>
      <StatGridStyled>
        <InvestigationStatTile
          label={getLabel("total")}
          value={summary.seatsCount ?? summary.passengersCount ?? NO_VALUE_DASH}
        />
        <InvestigationStatTile label={getLabel("peakOccupancy")} value={peak} />
        <InvestigationStatTile
          label={getLabel("boarded")}
          value={summary.boardedCount}
        />
        <InvestigationStatTile
          label={getLabel("noShow")}
          value={summary.noShowCount}
        />
      </StatGridStyled>

      {occupancyPath && (
        <PanelSectionStyled>
          <PanelSectionTitleStyled>
            {getLabel("occupancyOverTime")}
          </PanelSectionTitleStyled>
          <SparkWrapStyled>
            <InvestigationSparkline path={occupancyPath} color={blueMui._500} />
            <div className="spark-caption">
              <span>0</span>
              <span>{peak}</span>
            </div>
          </SparkWrapStyled>
        </PanelSectionStyled>
      )}

      {speedPath && (
        <PanelSectionStyled>
          <PanelSectionTitleStyled>
            {getLabel("speedOverTime")}
          </PanelSectionTitleStyled>
          <SparkWrapStyled>
            <InvestigationSparkline path={speedPath} color={greenMui._600} />
          </SparkWrapStyled>
        </PanelSectionStyled>
      )}
    </>
  );
};
